import type { DecodePuzzleDefinition, DecodeSegmentView } from '@aztec-realms/shared-types';

export type DecodePort = 'inner' | 'outer' | 'clockwise' | 'counter_clockwise';

export interface DecodeSegmentVisualState {
  aligned?: boolean;
  depth: number;
  energized: boolean;
  interrupted: boolean;
  terminal: boolean;
  terminalReached: boolean;
}

export interface DecodeVisualConnection {
  channel: 'jade' | 'gold';
  leftPort: DecodePort;
  leftSegmentId: string;
  rightPort: DecodePort;
  rightSegmentId: string;
}

export interface DecodeVisualState {
  connections: readonly DecodeVisualConnection[];
  energizedSegmentIds: ReadonlySet<string>;
  maxDepth: number;
  segments: Readonly<Record<string, DecodeSegmentVisualState>>;
}

const portOrder: readonly DecodePort[] = ['inner', 'clockwise', 'outer', 'counter_clockwise'];

const basePorts: Record<DecodeSegmentView['channelShape'], readonly DecodePort[]> = {
  straight: ['inner', 'outer'],
  corner: ['inner', 'clockwise'],
  fork: ['inner', 'clockwise', 'outer'],
};

function normalizeRotation(rotation: number): number {
  return ((Math.round(rotation) % 4) + 4) % 4;
}

export function portsForSegment(
  segment: DecodeSegmentView,
  rotation: number,
): ReadonlySet<DecodePort> {
  return new Set(channelsForSegment(segment, rotation).keys());
}

export function channelsForSegment(
  segment: DecodeSegmentView,
  rotation: number,
): ReadonlyMap<DecodePort, 'jade' | 'gold'> {
  const offset = normalizeRotation(rotation);
  return new Map(
    basePorts[segment.channelShape].map((port) => {
      const index = portOrder.indexOf(port);
      const rotated = portOrder[(index + offset) % portOrder.length] ?? port;
      return [rotated, segment.channelTypes?.[port] ?? 'jade'] as const;
    }),
  );
}

function segmentKey(ringIndex: number, angularSlot: number): string {
  return `${ringIndex}:${angularSlot}`;
}

function angularSlotAt(puzzle: DecodePuzzleDefinition, ringIndex: number, angle: number): number {
  const slots = Math.max(1, puzzle.sectorsPerRing[ringIndex] ?? 1);
  const normalized = ((angle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
  return Math.floor(normalized / ((Math.PI * 2) / slots)) % slots;
}

function segmentCenterAngle(puzzle: DecodePuzzleDefinition, segment: DecodeSegmentView): number {
  const slots = Math.max(1, puzzle.sectorsPerRing[segment.ringIndex] ?? 1);
  return ((segment.angularSlot + 0.5) / slots) * Math.PI * 2;
}

/**
 * Derives presentation-only energy flow from visible channel geometry.
 *
 * This function never decides puzzle completion or rewards. The server-owned solved
 * snapshot may drive celebration UI, but it cannot invent visual connectivity here.
 * Explicit target rotations are honored solely by deterministic local visual fixtures.
 */
export function deriveDecodeVisualState(
  puzzle: DecodePuzzleDefinition,
  rotations: Readonly<Record<string, number>>,
): DecodeVisualState {
  const byPosition = new Map<string, DecodeSegmentView>();
  const channels = new Map<string, ReadonlyMap<DecodePort, 'jade' | 'gold'>>();
  const adjacency = new Map<string, Set<string>>();
  const connections: DecodeVisualConnection[] = [];
  const eligible = new Set<string>();
  const terminalIds = new Set(puzzle.terminalSegmentIds);
  const sourceIds = new Set(puzzle.sourceSegmentIds ?? []);

  for (const segment of puzzle.segments) {
    byPosition.set(segmentKey(segment.ringIndex, segment.angularSlot), segment);
    const rotation = rotations[segment.id] ?? segment.rotation;
    channels.set(segment.id, channelsForSegment(segment, rotation));
    adjacency.set(segment.id, new Set());
    // Visible energy follows visible geometry. Local fixture targets are useful
    // for deterministic labels, but must never suppress a physically connected
    // intermediate path or teach a hidden-orientation rule.
    eligible.add(segment.id);
  }

  const connect = (
    left: DecodeSegmentView,
    leftPort: DecodePort,
    right: DecodeSegmentView,
    rightPort: DecodePort,
  ) => {
    if (!eligible.has(left.id) || !eligible.has(right.id)) return;
    const leftChannel = channels.get(left.id)?.get(leftPort);
    const rightChannel = channels.get(right.id)?.get(rightPort);
    if (!leftChannel || leftChannel !== rightChannel) return;
    adjacency.get(left.id)?.add(right.id);
    adjacency.get(right.id)?.add(left.id);
    connections.push({
      channel: leftChannel,
      leftPort,
      leftSegmentId: left.id,
      rightPort,
      rightSegmentId: right.id,
    });
  };

  for (const segment of puzzle.segments) {
    if (!eligible.has(segment.id)) continue;
    const segmentChannels = channels.get(segment.id);
    const slots = Math.max(1, puzzle.sectorsPerRing[segment.ringIndex] ?? 1);

    if (segmentChannels?.has('clockwise')) {
      const neighbor = byPosition.get(
        segmentKey(segment.ringIndex, (segment.angularSlot + 1) % slots),
      );
      if (neighbor && channels.get(neighbor.id)?.has('counter_clockwise'))
        connect(segment, 'clockwise', neighbor, 'counter_clockwise');
    }

    if (segmentChannels?.has('outer') && segment.ringIndex < puzzle.ringCount - 1) {
      const nextRing = segment.ringIndex + 1;
      const neighbor = byPosition.get(
        segmentKey(nextRing, angularSlotAt(puzzle, nextRing, segmentCenterAngle(puzzle, segment))),
      );
      if (neighbor && channels.get(neighbor.id)?.has('inner'))
        connect(segment, 'outer', neighbor, 'inner');
    }
  }

  const queue: Array<{ depth: number; segment: DecodeSegmentView }> = [];
  const depthById = new Map<string, number>();
  for (const segment of puzzle.segments) {
    if (
      segment.ringIndex === 0 &&
      (sourceIds.size === 0 || sourceIds.has(segment.id)) &&
      eligible.has(segment.id) &&
      channels.get(segment.id)?.has('inner')
    ) {
      depthById.set(segment.id, 1);
      queue.push({ depth: 1, segment });
    }
  }

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) break;
    for (const neighborId of adjacency.get(current.segment.id) ?? []) {
      if (depthById.has(neighborId)) continue;
      const neighbor = puzzle.segments.find((segment) => segment.id === neighborId);
      if (!neighbor) continue;
      const depth = current.depth + 1;
      depthById.set(neighborId, depth);
      queue.push({ depth, segment: neighbor });
    }
  }

  const visualSegments: Record<string, DecodeSegmentVisualState> = {};
  let maxDepth = 0;
  for (const segment of puzzle.segments) {
    const rotation = rotations[segment.id] ?? segment.rotation;
    const aligned =
      segment.targetRotation === undefined
        ? undefined
        : normalizeRotation(rotation) === normalizeRotation(segment.targetRotation);
    const depth = depthById.get(segment.id) ?? 0;
    const energized = depth > 0;
    const terminal = terminalIds.has(segment.id);
    const terminalReached =
      terminal && energized && channels.get(segment.id)?.has('outer') === true;
    maxDepth = Math.max(maxDepth, depth);
    visualSegments[segment.id] = {
      aligned,
      depth,
      energized,
      interrupted: aligned === false,
      terminal,
      terminalReached,
    };
  }

  return {
    connections,
    energizedSegmentIds: new Set(
      Object.entries(visualSegments)
        .filter(([, state]) => state.energized)
        .map(([segmentId]) => segmentId),
    ),
    maxDepth,
    segments: visualSegments,
  };
}
